import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  open: boolean;
  errorMessages: string[];
  validationResults: any[];
  onClose: () => void;
}

export const PackValidationDialog: React.FC<Props> = ({
  open,
  errorMessages,
  validationResults,
  onClose
}) => {
  const invalidSlots = validationResults.filter(s => !s.isValid);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h6">Pack Configuration Errors</Typography>
          <Chip
            label={`${invalidSlots.length} Invalid Slots`}
            color="error"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          This pack has invalid slot configurations that will prevent users from
          receiving player cards. Please fix the errors below before enabling the pack.
        </Alert>

        <Typography variant="subtitle2" gutterBottom>
          Error Summary:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          {errorMessages.map((error, idx) => (
            <Typography component="li" key={idx} variant="body2" color="error">
              {error}
            </Typography>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Detailed Slot Analysis:
        </Typography>

        {invalidSlots.map(slot => (
          <Accordion key={slot.slotIndex}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Slot {slot.slotIndex + 1} ({slot.slotType}) - {slot.summary}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {slot.dropProbabilityResults
                .filter(dp => !dp.isValid)
                .map(dp => (
                  <Box key={dp.dropProbabilityIndex} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Configuration {dp.dropProbabilityIndex + 1}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • OVR Range: {dp.ovrMin} - {dp.ovrMax}
                    </Typography>
                    {dp.playerPosition?.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        • Positions: {dp.playerPosition.join(', ')}
                      </Typography>
                    )}
                    {dp.tags?.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        • Tags: {dp.tags.join(', ')}
                      </Typography>
                    )}
                    <Typography variant="body2" fontWeight="bold" color="error">
                      • Players Available: {dp.playerCount}
                    </Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {dp.errorMessage}
                    </Alert>
                  </Box>
                ))}
            </AccordionDetails>
          </Accordion>
        ))}

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>How to fix:</strong>
          <ul>
            <li>Widen the OVR range to include more players</li>
            <li>Remove or broaden position filters</li>
            <li>Remove or broaden tag filters</li>
            <li>Check if players in this range exist in the system</li>
          </ul>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close and Fix Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};
